import * as Blockly from 'blockly/core';

const SLIDER_WIDTH = 130;
const SLIDER_HEIGHT = 42;
const TRACK_HEIGHT = 4;
const THUMB_RADIUS = 7;
const VALUE_AREA_WIDTH = 50;
const PADDING = 20;
const GAP = 8;
const SLIDER_AREA_WIDTH = PADDING + SLIDER_WIDTH + PADDING;
const TOTAL_WIDTH = SLIDER_AREA_WIDTH + GAP + VALUE_AREA_WIDTH;

export interface FieldSliderOptions {
    min: number;
    max: number;
    step?: number;
    suffix?: string;
}

export class FieldSlider extends Blockly.Field<number> {
    private min_: number;
    private max_: number;
    private step_: number;
    private suffix_: string;
    private currentValue_: number;
    private trackEl_: SVGRectElement | null = null;
    private fillEl_: SVGRectElement | null = null;
    private thumbEl_: SVGCircleElement | null = null;
    private valueTextEl_: SVGTextElement | null = null;
    private valueBgEl_: SVGRectElement | null = null;
    private isDragging_ = false;

    constructor(value: number, options: FieldSliderOptions) {
        super(value);
        if (!Number.isFinite(options.min) || !Number.isFinite(options.max) || options.max <= options.min) {
            throw new Error('FieldSlider requires max > min');
        }
        const step = options.step ?? 1;
        if (!Number.isFinite(step) || step <= 0) {
            throw new Error('FieldSlider requires step > 0');
        }
        this.min_ = options.min;
        this.max_ = options.max;
        this.step_ = step;
        this.suffix_ = options.suffix ?? '';
        this.currentValue_ = this.snapToStep_(Math.min(Math.max(value, this.min_), this.max_));
        this.SERIALIZABLE = true;
    }

    static override fromJson(
        options: Blockly.FieldConfig & { value?: number; min?: number; max?: number; step?: number; suffix?: string },
    ): FieldSlider {
        return new FieldSlider(options.value ?? 0, {
            min: options.min ?? 0,
            max: options.max ?? 100,
            step: options.step,
            suffix: options.suffix,
        });
    }

    override initView(): void {
        this.borderRect_ = Blockly.utils.dom.createSvgElement(
            Blockly.utils.Svg.RECT,
            { rx: 6, ry: 6, x: 0, y: 0, width: SLIDER_AREA_WIDTH, height: SLIDER_HEIGHT },
            this.fieldGroup_,
        );
        this.borderRect_.setAttribute('style', 'fill: rgba(0,0,0,0.2); stroke: rgba(0,0,0,0.3); stroke-width: 1;');

        const trackY = SLIDER_HEIGHT / 2 - TRACK_HEIGHT / 2;

        this.trackEl_ = Blockly.utils.dom.createSvgElement(
            Blockly.utils.Svg.RECT,
            { x: PADDING, y: trackY, width: SLIDER_WIDTH, height: TRACK_HEIGHT, rx: 2, ry: 2 },
            this.fieldGroup_,
        ) as SVGRectElement;
        this.trackEl_.setAttribute('style', 'fill: rgba(255,255,255,0.3);');

        this.fillEl_ = Blockly.utils.dom.createSvgElement(
            Blockly.utils.Svg.RECT,
            { x: PADDING, y: trackY, width: 0, height: TRACK_HEIGHT, rx: 2, ry: 2 },
            this.fieldGroup_,
        ) as SVGRectElement;
        this.fillEl_.setAttribute('style', 'fill: rgba(255,255,255,0.85);');

        this.thumbEl_ = Blockly.utils.dom.createSvgElement(
            Blockly.utils.Svg.CIRCLE,
            { cx: PADDING, cy: SLIDER_HEIGHT / 2, r: THUMB_RADIUS },
            this.fieldGroup_,
        ) as SVGCircleElement;
        this.thumbEl_.setAttribute(
            'style',
            'fill: white; stroke: rgba(0,0,0,0.2); stroke-width: 1; cursor: ew-resize;',
        );

        this.valueBgEl_ = Blockly.utils.dom.createSvgElement(
            Blockly.utils.Svg.RECT,
            { rx: 6, ry: 6, x: SLIDER_AREA_WIDTH + GAP, y: 0, width: VALUE_AREA_WIDTH, height: SLIDER_HEIGHT },
            this.fieldGroup_,
        ) as SVGRectElement;

        this.valueTextEl_ = Blockly.utils.dom.createSvgElement(
            Blockly.utils.Svg.TEXT,
            {
                x: SLIDER_AREA_WIDTH + GAP + VALUE_AREA_WIDTH / 2,
                y: SLIDER_HEIGHT / 2 + 1,
                'text-anchor': 'middle',
                'dominant-baseline': 'middle',
                'font-size': '16px',
                'font-weight': '600',
                'font-family': 'Cantarell, sans-serif',
                style: 'fill: white;',
            },
            this.fieldGroup_,
        ) as SVGTextElement;

        this.updateDisplay_();
        this.sliderBindEvents_();

        requestAnimationFrame(() => {
            const colour = this.getSourceBlock()?.getColour() ?? '#FF6D00';
            this.valueBgEl_?.setAttribute('style', `fill: ${colour}; stroke: rgba(0,0,0,0.3); stroke-width: 1;`);
        });
    }

    private snapToStep_(value: number): number {
        const stepped = Math.round((value - this.min_) / this.step_) * this.step_ + this.min_;
        const clamped = Math.min(Math.max(stepped, this.min_), this.max_);

        const decimals = (String(this.step_).split('.')[1] ?? '').length;
        return Number(clamped.toFixed(decimals));
    }

    private valueToX_(value: number): number {
        const ratio = (value - this.min_) / (this.max_ - this.min_);
        return PADDING + ratio * SLIDER_WIDTH;
    }

    private xToValue_(svgX: number): number {
        const ratio = Math.min(Math.max((svgX - PADDING) / SLIDER_WIDTH, 0), 1);
        const raw = this.min_ + ratio * (this.max_ - this.min_);
        return this.snapToStep_(raw);
    }

    private updateDisplay_(): void {
        const thumbX = this.valueToX_(this.currentValue_);
        const fillWidth = thumbX - PADDING;
        this.thumbEl_?.setAttribute('cx', String(thumbX));
        this.fillEl_?.setAttribute('width', String(Math.max(0, fillWidth)));
        if (this.valueTextEl_) {
            this.valueTextEl_.textContent = `${this.currentValue_}${this.suffix_}`;
        }
    }

    private clientXToSvgX_(clientX: number): number {
        const svgEl = this.fieldGroup_?.ownerSVGElement;
        if (!svgEl) return 0;
        const ctm = (this.fieldGroup_ as SVGGraphicsElement).getScreenCTM();
        if (!ctm) return 0;
        const pt = svgEl.createSVGPoint();
        pt.x = clientX;
        pt.y = 0;
        return pt.matrixTransform(ctm.inverse()).x;
    }

    private sliderBindEvents_(): void {
        if (!this.fieldGroup_) return;

        const onPointerDown = (e: PointerEvent) => {
            if (!this.isCurrentlyEditable()) return;
            e.stopPropagation();
            this.isDragging_ = true;
            this.thumbEl_?.setAttribute(
                'style',
                'fill: white; stroke: rgba(0,0,0,0.3); stroke-width: 2; cursor: ew-resize;',
            );
            this.applyValue_(this.clientXToSvgX_(e.clientX));

            const onMove = (e: PointerEvent) => {
                if (!this.isDragging_) return;
                e.stopPropagation();
                this.applyValue_(this.clientXToSvgX_(e.clientX));
            };
            const onUp = () => {
                this.isDragging_ = false;
                this.thumbEl_?.setAttribute(
                    'style',
                    'fill: white; stroke: rgba(0,0,0,0.2); stroke-width: 1; cursor: ew-resize;',
                );
                window.removeEventListener('pointermove', onMove as EventListener);
                window.removeEventListener('pointerup', onUp);
            };
            window.addEventListener('pointermove', onMove as EventListener);
            window.addEventListener('pointerup', onUp);
        };

        for (const el of [this.trackEl_, this.fillEl_, this.thumbEl_]) {
            el?.addEventListener('pointerdown', onPointerDown as EventListener);
        }

        this.valueBgEl_?.addEventListener('pointerdown', (e) => {
            e.stopPropagation();
        });
        this.valueBgEl_?.addEventListener('click', (e) => {
            e.stopPropagation();
            this.showEditor_();
        });
        this.valueTextEl_?.addEventListener('pointerdown', (e) => {
            e.stopPropagation();
        });
        this.valueTextEl_?.addEventListener('click', (e) => {
            e.stopPropagation();
            this.showEditor_();
        });
    }

    private applyValue_(svgX: number): void {
        const newValue = this.xToValue_(svgX);
        if (newValue !== this.currentValue_) {
            this.setValue(newValue);
        }
    }

    override showEditor_(): void {
        if (!this.isCurrentlyEditable()) return;
        document.getElementById('_fieldSliderInput')?.remove();

        const fieldRect = this.fieldGroup_?.getBoundingClientRect();
        if (!fieldRect) return;

        const container = document.createElement('div');
        container.id = '_fieldSliderInput';
        container.style.cssText = `
            position: fixed;
            left: ${fieldRect.right - VALUE_AREA_WIDTH - 10}px;
            top: ${fieldRect.bottom + 4}px;
            z-index: 99999;
            background: ${this.getSourceBlock()?.getColour() ?? '#FF6D00'};
            border-radius: 8px;
            padding: 8px 10px;
            display: flex;
            align-items: center;
            gap: 6px;
            box-shadow: 0 2px 8px rgba(0,0,0,0.3);
        `;

        const input = document.createElement('input');
        input.type = 'number';
        input.min = String(this.min_);
        input.max = String(this.max_);
        input.step = String(this.step_);
        input.value = String(this.currentValue_);
        input.style.cssText = `
            width: 60px; font-size: 14px; padding: 4px 6px;
            border: none; border-radius: 4px;
            font-family: Cantarell, sans-serif;
            font-weight: 600;
        `;

        const label = document.createElement('span');
        label.textContent = this.suffix_ || '';
        label.style.cssText = 'font-size: 13px; color: white; font-weight: 500;';

        container.appendChild(input);
        container.appendChild(label);
        document.body.appendChild(container);

        requestAnimationFrame(() => {
            input.focus();
            input.select();
        });

        let onOut: ((e: PointerEvent) => void) | null = null;
        const cleanupOutsideListener = () => {
            if (onOut) {
                window.removeEventListener('pointerdown', onOut as EventListener, true);
                onOut = null;
            }
        };

        let committed = false;
        const commit = () => {
            if (committed) return;
            committed = true;
            const raw = parseFloat(input.value);
            if (!isNaN(raw)) {
                this.setValue(this.snapToStep_(raw));
            }
            container.remove();
            cleanupOutsideListener();
        };

        input.addEventListener('keydown', (e) => {
            if (e.key === 'Enter') commit();
            if (e.key === 'Escape') {
                committed = true;
                container.remove();
                cleanupOutsideListener();
            }
        });

        requestAnimationFrame(() => {
            if (committed) return;
            onOut = (e: PointerEvent) => {
                if (!container.contains(e.target as Node)) {
                    commit();
                }
            };
            window.addEventListener('pointerdown', onOut as EventListener, true);
        });
    }

    override getValue(): number {
        return this.currentValue_;
    }

    override doValueUpdate_(newValue: number): void {
        this.currentValue_ = this.snapToStep_(Number(newValue));
        this.updateDisplay_();
    }

    override saveState(): number {
        return this.currentValue_;
    }

    override loadState(state: number): void {
        this.currentValue_ = this.snapToStep_(Number(state));
        this.updateDisplay_();
    }

    override getSize(): Blockly.utils.Size {
        return new Blockly.utils.Size(TOTAL_WIDTH, SLIDER_HEIGHT);
    }

    override getDisplayText_(): string {
        return `${this.currentValue_}${this.suffix_}`;
    }
}
